import { Injectable } from '@angular/core';
import { DataNoRowOverlayComponent } from '@examdojo/ag-grid';
import { BigQueryHttpService, BigQueryTableMetadata } from '@examdojo/auth';
import { DateTimeFormat, DateTimeService } from '@examdojo/core/date-time';
import { getVariableColor, VariableColor } from '@examdojo/styles';
import {
  ColDef,
  ColGroupDef,
  GridOptions,
  IAggFuncParams,
  ICellRendererParams,
  IRowNode,
  ValueFormatterParams,
} from 'ag-grid-community';
import { Observable, of } from 'rxjs';
import { catchError, map, startWith } from 'rxjs/operators';

type iso_date = string; // e.g. "2025-09-15" (week-start ISO date)
type weekly_series = Record<iso_date, number>; // all series aggregated weekly

export interface ContentAnalyticsData {
  syllabus_id: number;
  syllabus_short_name: string;
  program: string;
  start_date: string;
  launch_date: string;
  estimated_launch_date: string;
  next_exam_session: string;
  next_exam_session_estimated: boolean;
  readiness_score: number;
  completeness_score: number;
  sources: {
    'Tier 1': number;
    'Tier 2': number;
    'Tier 3': number;
    completion_rate: number;
    parsed_rate: number;
    success_rate: number;
  };
  cumulative_rqs: weekly_series;
  cumulative_dqs: weekly_series;
  reference_questions: {
    total_rqs: number;
    completion_rate: number;
    success_rate: number;
  };
  derivative_questions: {
    total_dqs: number;
    q_completion_rate: number;
    q_success_rate: number;
    avg_published_dqs_per_rq: number;
  };
  topics: {
    TL1: number;
    TL2: number;
    TL3: number;
  };
  cumulative_costs_in_eur: weekly_series;
  total_spend: number;
  avg_costs_in_eur: {
    human_per_rq: number;
    human_per_dq: number;
    per_approved_rq: number;
    ai_per_rq: number;
    ai_per_dq: number;
    per_published_dq: number;
  };
  coverage: {
    rq_to_tl3: number;
    dq_to_tl3: number;
    source_to_rq: number;
    dq_to_rq: number;
  };
}

@Injectable()
export class ContentAnalyticsAgGridService {
  constructor(
    private readonly dateTimeService: DateTimeService,
    private readonly bigQueryService: BigQueryHttpService,
  ) {}

  readonly gridOptions: GridOptions<ContentAnalyticsData> = {
    suppressCellFocus: true,
    suppressContextMenu: true,
    groupDisplayType: 'singleColumn',
    groupDefaultExpanded: -1,
    groupMaintainOrder: false,
    noRowsOverlayComponent: DataNoRowOverlayComponent,
    noRowsOverlayComponentParams: {
      text: 'No analytics data found',
      body: 'Please check back later for analytics data',
    },
    rowClass: 'cursor-pointer',
    animateRows: true,
    tooltipShowDelay: 300,
    autoGroupColumnDef: {
      headerName: 'Program',
      width: 180,
      minWidth: 180,
      maxWidth: 220,
      pinned: 'left',
      sortable: true,
      cellRendererParams: {
        innerRenderer: (params: ICellRendererParams<ContentAnalyticsData, string>) => {
          // For pinned bottom row (totals), don't show status tag
          if (params.data?.program === 'TOTAL') {
            return '';
          }

          // For group rows (program level), just show the program name
          if (params.node.group) {
            return params.value;
          }

          // For leaf rows (syllabus level), show status tag
          const hasCompleteness =
            params.data?.completeness_score !== null && params.data?.completeness_score !== undefined;

          if (hasCompleteness) {
            return `<span class="px-2 py-0.5 rounded text-xs font-semibold whitespace-nowrap bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">Active</span>`;
          } else {
            return `<span class="px-2 py-0.5 rounded text-xs font-semibold whitespace-nowrap bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200">In Dev</span>`;
          }
        },
      },
    },
    suppressAggFuncInHeader: true,
    groupAggFiltering: true,
    alwaysAggregateAtRootLevel: true,
    multiSortKey: 'ctrl',
    allowContextMenuWithControlKey: true,
    pinnedBottomRowData: [],
    defaultColDef: {
      suppressHeaderMenuButton: true,
      width: 150,
      minWidth: 120,
      suppressMovable: true,
      menuTabs: [],
      resizable: true,
      sortable: true,
      floatingFilter: false,
      sortingOrder: ['asc', 'desc', null],
      unSortIcon: false,
    },
    includeHiddenColumnsInQuickFilter: true,
    quickFilterText: '',
  };

  get columnDefs(): Array<ColDef<ContentAnalyticsData> | ColGroupDef<ContentAnalyticsData>> {
    const isDarkMode = typeof document !== 'undefined' && document.documentElement.classList.contains('dark');
    const rqsColor = getVariableColor(VariableColor.Primary400, isDarkMode);
    const dqsColor = getVariableColor(VariableColor.AssetHealthHealthy, isDarkMode);

    return [
      {
        field: 'program',
        hide: true,
        rowGroup: true,
      },
      {
        field: 'syllabus_short_name',
        headerName: 'Syllabus',
        width: 180,
        minWidth: 160,
        pinned: 'left',
        cellClass: 'font-medium text-gray-900 dark:text-gray-100',
        filterParams: {
          filterOptions: ['contains'],
          maxNumConditions: 1,
        },
      },
      {
        field: 'readiness_score',
        headerName: 'Score',
        headerTooltip:
          'Shows completeness score for launched syllabuses or readiness score for pre-launch syllabuses. Both scores are from 0-100% and combine metrics from RQ, DQ & Learn Hub.',
        width: 150,
        minWidth: 130,
        valueGetter: (params) => {
          // Prioritize completeness_score if available (not null/undefined)
          const completenessScore = params.data?.completeness_score;
          const readinessScore = params.data?.readiness_score;

          if (completenessScore !== null && completenessScore !== undefined) {
            return completenessScore;
          }
          return readinessScore ?? 0;
        },
        cellRenderer: (params: { value: number; data: ContentAnalyticsData }) => {
          if (params.value === null || params.value === undefined) {
            return '';
          }

          return this.getProgressBarHTML(params.value, true);
        },
        filter: 'agNumberColumnFilter',
        filterParams: {
          filterOptions: ['greaterThan', 'lessThan', 'equals'],
          maxNumConditions: 2,
        },
      },
      {
        field: 'topics',
        headerName: 'Topics',
        width: 150,
        minWidth: 130,
        cellRenderer: (params: { value: ContentAnalyticsData['topics'] }) => {
          const topics = params.value;
          if (!topics) {
            return '';
          }

          const parts = [];
          if (topics.TL1 !== undefined) {
            parts.push(`TL1: <span class="text-black dark:text-white">${topics.TL1}</span>`);
          }
          if (topics.TL2 !== undefined) {
            parts.push(`TL2: <span class="text-black dark:text-white">${topics.TL2}</span>`);
          }
          if (topics.TL3 !== undefined) {
            parts.push(`TL3: <span class="text-black dark:text-white">${topics.TL3}</span>`);
          }

          return `<div class="flex items-center justify-start h-full text-xs text-gray-600 dark:text-gray-400 leading-tight">
          ${parts.join(', ')}
        </div>`;
        },
      },
      {
        headerName: 'Timing',
        children: [
          {
            field: 'launch_date',
            headerName: 'Launch Date',
            headerTooltip:
              'The date when the syllabus was made active and available to users. Shows an estimated date if the syllabus is not yet active.',
            width: 140,
            minWidth: 120,
            aggFunc: (params: IAggFuncParams<ContentAnalyticsData>) => {
              // Find the earliest (minimum) launch date from the values returned by valueGetter
              const values = params.values as Array<{ date: string; isEstimated: boolean } | null>;

              const validValues = values
                .filter((val) => val && val.date)
                .map((val) => ({
                  original: val!,
                  dateObj: this.dateTimeService.parseDate(val!.date),
                }))
                .filter((item) => item.dateObj !== null)
                .sort((a, b) => a.dateObj!.getTime() - b.dateObj!.getTime());

              return validValues[0]?.original ?? null;
            },
            cellClass: 'text-gray-700 dark:text-gray-300',
            valueGetter: (params) => {
              // Prioritize launch_date over estimated_launch_date
              const launchDate = params.data?.launch_date;
              const estimatedDate = params.data?.estimated_launch_date;

              if (launchDate) {
                return { date: launchDate, isEstimated: false };
              } else if (estimatedDate) {
                return { date: estimatedDate, isEstimated: true };
              }
              return null;
            },
            valueFormatter: (params: ValueFormatterParams) => {
              if (!params.value || typeof params.value !== 'object') {
                return '';
              }

              const { date, isEstimated } = params.value as { date: string; isEstimated: boolean };
              if (!date) {
                return '';
              }

              // For estimated dates, show relative days in the cell
              if (isEstimated) {
                return this.dateTimeService.formatDateRelativeToNowShort(date);
              }

              // For actual launch dates, show the formatted date
              return this.dateTimeService.formatDate(date, DateTimeFormat.ShortDate);
            },
            tooltipValueGetter: (params) => {
              if (!params.value || typeof params.value !== 'object') {
                return '';
              }

              const { date, isEstimated } = params.value as { date: string; isEstimated: boolean };
              if (!date) {
                return '';
              }

              // For estimated dates, show the actual date
              if (isEstimated) {
                return this.dateTimeService.formatDate(date, DateTimeFormat.ShortDate);
              }

              // For actual launch dates, show relative days in the tooltip
              return this.dateTimeService.formatDateRelativeToNowShort(date);
            },
          },
          {
            field: 'next_exam_session',
            headerName: 'Next Exam',
            headerTooltip: 'The next exam session date for this syllabus.',
            width: 140,
            minWidth: 120,
            aggFunc: (params: IAggFuncParams<ContentAnalyticsData>) => {
              // Find the earliest (minimum) next exam session date from the values returned by valueGetter
              const values = params.values as Array<{ date: string; isEstimated: boolean } | null>;

              const validValues = values
                .filter((val) => val && val.date)
                .map((val) => ({
                  original: val!,
                  dateObj: this.dateTimeService.parseDate(val!.date),
                }))
                .filter((item) => item.dateObj !== null)
                .sort((a, b) => a.dateObj!.getTime() - b.dateObj!.getTime());

              return validValues[0]?.original ?? null;
            },
            cellClass: 'text-gray-700 dark:text-gray-300',
            valueGetter: (params) => {
              const nextExamSession = params.data?.next_exam_session;
              const isEstimated = params.data?.next_exam_session_estimated ?? false;

              if (nextExamSession) {
                return { date: nextExamSession, isEstimated };
              }
              return null;
            },
            valueFormatter: (params: ValueFormatterParams) => {
              if (!params.value || typeof params.value !== 'object') {
                return '';
              }

              const { date } = params.value as { date: string; isEstimated: boolean };
              if (!date) {
                return '';
              }

              return this.dateTimeService.formatDateRelativeToNowShort(date);
            },
            tooltipValueGetter: (params) => {
              if (!params.value || typeof params.value !== 'object') {
                return '';
              }

              const { date } = params.value as { date: string; isEstimated: boolean };
              if (!date) {
                return '';
              }

              return this.dateTimeService.formatDate(date, DateTimeFormat.ShortDate);
            },
          },
          {
            field: 'start_date',
            headerName: 'Start Date',
            headerTooltip: 'The date when the syllabus was created in the database.',
            width: 140,
            minWidth: 120,
            aggFunc: (params) => {
              // Find the earliest (minimum) start date
              const rowDataItems = params.rowNode.allLeafChildren?.map((child) => child.data).filter(Boolean) ?? [];

              const validDates = rowDataItems
                .map((row) => row?.start_date)
                .filter((date) => date && typeof date === 'string');

              if (validDates.length === 0) {
                return null;
              }

              // Convert to Date objects using DateTimeService, sort by date value, then return the earliest date string
              return (
                validDates
                  .map((dateStr) => ({
                    dateStr,
                    dateObj: this.dateTimeService.parseDate(dateStr as string),
                  }))
                  .filter((item) => item.dateObj !== null)
                  .sort((a, b) => a.dateObj!.getTime() - b.dateObj!.getTime())[0]?.dateStr ?? null
              );
            },
            columnGroupShow: 'open',
            cellClass: 'text-gray-700 dark:text-gray-300',
            valueFormatter: (params: ValueFormatterParams) => {
              if (!params.value) {
                return '';
              }
              return this.dateTimeService.formatDate(params.value, DateTimeFormat.ShortDate);
            },
            tooltipValueGetter: (params) => {
              if (!params.value) {
                return '';
              }

              return this.dateTimeService.formatDateRelativeToNowShort(params.value as string);
            },
          },
          {
            headerName: 'Launch Time',
            headerTooltip: 'The number of days between the start date and the launch date.',
            width: 120,
            minWidth: 100,
            columnGroupShow: 'open',
            aggFunc: 'avg',
            cellClass: 'text-gray-700 dark:text-gray-300',
            valueGetter: (params) => {
              const startDate = params.data?.start_date;
              const launchDate = params.data?.launch_date;
              const estimatedLaunchDate = params.data?.estimated_launch_date;

              // Use actual launch date if available, otherwise use estimated launch date
              const effectiveLaunchDate = launchDate || estimatedLaunchDate;

              if (!startDate || !effectiveLaunchDate) {
                return null;
              }

              const start = this.dateTimeService.parseDate(startDate);
              const launch = this.dateTimeService.parseDate(effectiveLaunchDate);

              if (!start || !launch) {
                return null;
              }

              start.setHours(0, 0, 0, 0);
              launch.setHours(0, 0, 0, 0);

              return Math.abs(this.dateTimeService.diff(launch, start, 'days'));
            },
            valueFormatter: (params: ValueFormatterParams) => {
              if (params.value === null || params.value === undefined) {
                return '';
              }
              return `${params.value} days`;
            },
            filter: 'agNumberColumnFilter',
            filterParams: {
              filterOptions: ['greaterThan', 'lessThan', 'equals'],
              maxNumConditions: 2,
            },
          },
        ],
      },
      {
        headerName: 'Sources',
        children: [
          {
            field: 'sources',
            headerName: 'Total Sources',
            width: 105,
            minWidth: 100,
            aggFunc: 'sum',
            cellClass: 'text-gray-700 dark:text-gray-300 font-medium',
            valueGetter: (params) => {
              const sources = params.data?.sources;
              if (!sources) {
                return 0;
              }
              return (sources['Tier 1'] || 0) + (sources['Tier 2'] || 0) + (sources['Tier 3'] || 0);
            },
            valueFormatter: (params: ValueFormatterParams) => {
              if (params.value === null || params.value === undefined) {
                return '';
              }
              return params.value.toLocaleString();
            },
          },
          {
            field: 'sources.Tier 1',
            headerName: '🥇 Tier 1',
            columnGroupShow: 'open',
            width: 100,
            minWidth: 80,
            aggFunc: 'sum',
            cellClass: 'text-gray-600 dark:text-gray-400',
            valueFormatter: (params: ValueFormatterParams) => {
              if (params.value === null || params.value === undefined) {
                return '';
              }
              return params.value.toLocaleString();
            },
          },
          {
            field: 'sources.Tier 2',
            headerName: '🥈 Tier 2',
            columnGroupShow: 'open',
            width: 100,
            minWidth: 80,
            aggFunc: 'sum',
            cellClass: 'text-gray-600 dark:text-gray-400',
            valueFormatter: (params: ValueFormatterParams) => {
              if (params.value === null || params.value === undefined) {
                return '';
              }
              return params.value.toLocaleString();
            },
          },
          {
            field: 'sources.Tier 3',
            headerName: '🥉 Tier 3',
            columnGroupShow: 'open',
            width: 100,
            minWidth: 80,
            aggFunc: 'sum',
            cellClass: 'text-gray-600 dark:text-gray-400',
            valueFormatter: (params: ValueFormatterParams) => {
              if (params.value === null || params.value === undefined) {
                return '';
              }
              return params.value.toLocaleString();
            },
          },
          {
            field: 'sources.parsed_rate',
            headerName: 'Parsed Rate',
            headerTooltip:
              'Percentage of sources that have been parsed successfully or unsuccessfully (in a terminal status).',
            columnGroupShow: 'open',
            width: 140,
            minWidth: 120,
            aggFunc: 'avg',
            cellRenderer: (params: { value: number }) => {
              if (params.value === null || params.value === undefined) {
                return '';
              }
              return this.getProgressBarHTML(params.value);
            },
          },
          {
            field: 'sources.completion_rate',
            headerName: 'Completion Rate',
            headerTooltip:
              'Percentage of sources in a terminal status (all RQ within a source are in a terminal status).',
            width: 105,
            minWidth: 100,
            aggFunc: 'avg',
            cellRenderer: (params: { value: number }) => {
              if (params.value === null || params.value === undefined) {
                return '';
              }
              return this.getProgressBarHTML(params.value);
            },
          },
          {
            field: 'sources.success_rate',
            headerName: 'Success Rate',
            headerTooltip:
              'Percentage of sources completed successfully (all RQ within a source have been parsed successfully "Human Approved").',
            columnGroupShow: 'open',
            width: 140,
            minWidth: 120,
            aggFunc: 'avg',
            cellRenderer: (params: { value: number }) => {
              if (params.value === null || params.value === undefined) {
                return '';
              }
              return this.getProgressBarHTML(params.value);
            },
          },
        ],
      },
      {
        headerName: 'Reference Questions (RQ)',
        children: [
          {
            field: 'cumulative_rqs',
            headerName: 'Approved RQs',
            headerTooltip: 'Reference questions with the status "Human Approved".',
            width: 120,
            minWidth: 100,
            aggFunc: 'sum',
            cellClass: 'text-gray-700 dark:text-gray-300 font-medium',
            valueGetter: (params) => {
              const rqs = params.data?.cumulative_rqs;
              if (!rqs) {
                return 0;
              }
              return Object.values(rqs).reduce((max, val) => Math.max(max, val), 0);
            },
            valueFormatter: (params: ValueFormatterParams) => {
              if (params.value === null || params.value === undefined) {
                return '';
              }
              return params.value.toLocaleString();
            },
          },
          {
            field: 'reference_questions.total_rqs',
            headerName: 'Total RQs',
            headerTooltip: 'Total count of Reference Questions across all statuses.',
            columnGroupShow: 'open',
            width: 120,
            minWidth: 100,
            aggFunc: 'sum',
            cellClass: 'text-gray-600 dark:text-gray-400',
            valueFormatter: (params: ValueFormatterParams) => {
              if (params.value === null || params.value === undefined) {
                return '';
              }
              return params.value.toLocaleString();
            },
          },
          {
            field: 'reference_questions.completion_rate',
            headerName: 'Completion Rate',
            headerTooltip:
              'Percentage of RQs in a terminal status: (Human Approved + Unfixable + Needs Topic Tagging) / Total RQs.',
            width: 150,
            minWidth: 130,
            aggFunc: 'avg',
            cellRenderer: (params: { value: number }) => {
              if (params.value === null || params.value === undefined) {
                return '';
              }
              return this.getProgressBarHTML(params.value);
            },
          },
          {
            field: 'reference_questions.success_rate',
            headerName: 'Success Rate',
            headerTooltip:
              'Percentage of RQs successfully processed: (Human Approved + Needs Topic Tagging) / Total RQs.',
            columnGroupShow: 'open',
            width: 140,
            minWidth: 120,
            aggFunc: 'avg',
            cellRenderer: (params: { value: number }) => {
              if (params.value === null || params.value === undefined) {
                return '';
              }
              return this.getProgressBarHTML(params.value);
            },
          },
          {
            field: 'cumulative_rqs',
            headerName: 'RQs Change',
            columnGroupShow: 'open',
            width: 100,
            minWidth: 80,
            sortable: false,
            aggFunc: (params) => {
              // Aggregate sparkline data by summing values for each date
              const rowDataItems = params.rowNode.allLeafChildren?.map((child) => child.data).filter(Boolean) ?? [];
              const dateValueMap = new Map<string, number>();
              let hasValidData = false;

              rowDataItems.forEach((row) => {
                if (row) {
                  const rqs = row.cumulative_rqs;
                  if (rqs && typeof rqs === 'object') {
                    hasValidData = true;
                    Object.entries(rqs).forEach(([date, value]) => {
                      if (typeof value === 'number') {
                        dateValueMap.set(date, (dateValueMap.get(date) ?? 0) + value);
                      }
                    });
                  }
                }
              });

              if (!hasValidData) {
                return undefined;
              }

              return Array.from(dateValueMap.entries())
                .map(([date, value]) => ({ date, value }))
                .sort((a, b) => a.date.localeCompare(b.date));
            },
            valueGetter: (params) => {
              const rqs = params.data?.cumulative_rqs;
              if (!rqs) {
                return undefined;
              }
              return Object.entries(rqs)
                .map(([date, value]) => ({ date, value }))
                .sort((a, b) => a.date.localeCompare(b.date));
            },
            cellRendererSelector: (p) => {
              const data = p.value as Array<{ date: string; value: number }> | undefined;
              if (Array.isArray(data) && data.length > 0) {
                return {
                  component: 'agSparklineCellRenderer',
                  params: {
                    sparklineOptions: {
                      type: 'area',
                      xKey: 'date',
                      yKey: 'value',
                      area: {
                        fill: rqsColor,
                        stroke: rqsColor,
                      },
                      highlightStyle: {
                        fill: rqsColor,
                      },
                      tooltip: {
                        enabled: true,
                        renderer: (tp: { xValue: string; yValue: number }) => {
                          // tp.xValue is already in ISO format (YYYY-MM-DD), convert to DD-MM-YYYY
                          const dateStr = tp.xValue.toString();
                          const [y, m, d] = dateStr.split('-');
                          const ddmmyyyy = d && m && y ? `${d}-${m}-${y}` : dateStr;
                          return { title: tp.yValue.toLocaleString(), content: ddmmyyyy };
                        },
                      },
                    },
                  },
                };
              }
              return undefined;
            },
          },
          {
            field: 'coverage.rq_to_tl3',
            headerName: 'RQ to TL3 Coverage (i)',
            headerTooltip: 'The percentage of published RQs that are covering TL3 topics.',
            columnGroupShow: 'open',
            width: 140,
            minWidth: 120,
            aggFunc: 'avg',
            cellRenderer: (params: { value: number }) => {
              if (params.value === null || params.value === undefined) {
                return '';
              }
              return this.getProgressBarHTML(params.value);
            },
          },
          {
            field: 'coverage.source_to_rq',
            headerName: 'Source to RQ Coverage (i)',
            headerTooltip: 'The percentage of published RQs compared to the total source questions.',
            columnGroupShow: 'open',
            width: 150,
            minWidth: 130,
            aggFunc: 'avg',
            cellRenderer: (params: { value: number }) => {
              if (params.value === null || params.value === undefined) {
                return '';
              }
              return this.getProgressBarHTML(params.value);
            },
          },
        ],
      },
      {
        headerName: 'Derivative Questions (DQ)',
        children: [
          {
            field: 'cumulative_dqs',
            headerName: 'Published DQs',
            headerTooltip: 'Derivative questions with the status "Published".',
            width: 120,
            minWidth: 100,
            aggFunc: 'sum',
            cellClass: 'text-gray-700 dark:text-gray-300 font-medium',
            valueGetter: (params) => {
              const dqs = params.data?.cumulative_dqs;
              if (!dqs) {
                return 0;
              }
              return Object.values(dqs).reduce((max, val) => Math.max(max, val), 0);
            },
            valueFormatter: (params: ValueFormatterParams) => {
              if (params.value === null || params.value === undefined) {
                return '';
              }
              return params.value.toLocaleString();
            },
          },
          {
            field: 'derivative_questions.total_dqs',
            headerName: 'Total DQs',
            headerTooltip: 'Total count of Derivative Questions across all statuses.',
            columnGroupShow: 'open',
            width: 120,
            minWidth: 100,
            aggFunc: 'sum',
            cellClass: 'text-gray-600 dark:text-gray-400',
            valueFormatter: (params: ValueFormatterParams) => {
              if (params.value === null || params.value === undefined) {
                return '';
              }
              return params.value.toLocaleString();
            },
          },
          {
            field: 'derivative_questions.q_completion_rate',
            headerName: 'Completion Rate',
            headerTooltip: 'Percentage of DQs in a terminal status: (Published + Deleted) / Total DQs.',
            width: 150,
            minWidth: 130,
            aggFunc: 'avg',
            cellRenderer: (params: { value: number }) => {
              if (params.value === null || params.value === undefined) {
                return '';
              }
              return this.getProgressBarHTML(params.value);
            },
          },
          {
            field: 'derivative_questions.q_success_rate',
            headerName: 'Success Rate',
            headerTooltip: 'Percentage of DQs successfully published: Published / Total DQs.',
            columnGroupShow: 'open',
            width: 140,
            minWidth: 120,
            aggFunc: 'avg',
            cellRenderer: (params: { value: number }) => {
              if (params.value === null || params.value === undefined) {
                return '';
              }
              return this.getProgressBarHTML(params.value);
            },
          },
          {
            field: 'cumulative_dqs',
            headerName: 'DQs Change',
            columnGroupShow: 'open',
            width: 100,
            minWidth: 80,
            sortable: false,
            aggFunc: (params) => {
              // Aggregate sparkline data by summing values for each date
              const rowDataItems = params.rowNode.allLeafChildren?.map((child) => child.data).filter(Boolean) ?? [];
              const dateValueMap = new Map<string, number>();
              let hasValidData = false;

              rowDataItems.forEach((row) => {
                if (row) {
                  const dqs = row.cumulative_dqs;
                  if (dqs && typeof dqs === 'object') {
                    hasValidData = true;
                    Object.entries(dqs).forEach(([date, value]) => {
                      if (typeof value === 'number') {
                        dateValueMap.set(date, (dateValueMap.get(date) ?? 0) + value);
                      }
                    });
                  }
                }
              });

              if (!hasValidData) {
                return undefined;
              }

              return Array.from(dateValueMap.entries())
                .map(([date, value]) => ({ date, value }))
                .sort((a, b) => a.date.localeCompare(b.date));
            },
            valueGetter: (params) => {
              const dqs = params.data?.cumulative_dqs;
              if (!dqs) {
                return undefined;
              }
              return Object.entries(dqs)
                .map(([date, value]) => ({ date, value }))
                .sort((a, b) => a.date.localeCompare(b.date));
            },
            cellRendererSelector: (p) => {
              const data = p.value as Array<{ date: string; value: number }> | undefined;
              if (Array.isArray(data) && data.length > 0) {
                return {
                  component: 'agSparklineCellRenderer',
                  params: {
                    sparklineOptions: {
                      type: 'area',
                      xKey: 'date',
                      yKey: 'value',
                      area: {
                        fill: dqsColor,
                        stroke: dqsColor,
                      },
                      highlightStyle: {
                        fill: dqsColor,
                      },
                      tooltip: {
                        enabled: true,
                        renderer: (tp: { xValue: string; yValue: number }) => {
                          // tp.xValue is already in ISO format (YYYY-MM-DD), convert to DD-MM-YYYY
                          const dateStr = tp.xValue.toString();
                          const [y, m, d] = dateStr.split('-');
                          const ddmmyyyy = d && m && y ? `${d}-${m}-${y}` : dateStr;
                          return { title: tp.yValue.toLocaleString(), content: ddmmyyyy };
                        },
                      },
                    },
                  },
                };
              }
              return undefined;
            },
          },
          {
            field: 'coverage.dq_to_tl3',
            headerName: 'DQ to TL3 Coverage (i)',
            headerTooltip: 'The percentage of two published DQs that are covering TL3 topics.',
            columnGroupShow: 'open',
            width: 140,
            minWidth: 120,
            aggFunc: 'avg',
            cellRenderer: (params: { value: number }) => {
              if (params.value === null || params.value === undefined) {
                return '';
              }
              return this.getProgressBarHTML(params.value);
            },
          },
          {
            field: 'coverage.dq_to_rq',
            headerName: 'DQ to RQ Coverage (i)',
            headerTooltip: 'The percentage of how many DQs have at least 1 Published Questions for each published RQs',
            columnGroupShow: 'open',
            width: 150,
            minWidth: 130,
            aggFunc: 'avg',
            cellRenderer: (params: { value: number }) => {
              if (params.value === null || params.value === undefined) {
                return '';
              }
              return this.getProgressBarHTML(params.value);
            },
          },
          {
            field: 'derivative_questions.avg_published_dqs_per_rq',
            headerName: 'Avg. Published DQs per RQ',
            headerTooltip: 'Average number of published DQs per successful RQ that has at least one published DQ.',
            columnGroupShow: 'open',
            width: 180,
            minWidth: 160,
            aggFunc: this.createAvgAggFunc((row) => row?.derivative_questions?.avg_published_dqs_per_rq),
            cellClass: 'text-gray-700 dark:text-gray-300',
            valueFormatter: (params: ValueFormatterParams) => {
              if (params.value === null || params.value === undefined || typeof params.value !== 'number') {
                return '';
              }
              return params.value.toFixed(2);
            },
          },
        ],
      },
      {
        headerName: 'Costs',
        children: [
          {
            field: 'total_spend',
            headerName: 'Total Costs',
            headerTooltip: 'Total spend for this syllabus (human + AI costs).',
            width: 120,
            minWidth: 100,
            aggFunc: 'sum',
            cellClass: 'text-gray-700 dark:text-gray-300 font-medium',
            valueFormatter: (params: ValueFormatterParams) => {
              if (params.value === null || params.value === undefined || typeof params.value !== 'number') {
                return '';
              }
              return `${params.value.toLocaleString()} EUR`;
            },
          },
          {
            field: 'avg_costs_in_eur.human_per_rq',
            headerName: 'Avg. Human costs per RQ',
            headerTooltip: 'Average human labor costs per Reference Question.',
            columnGroupShow: 'open',
            width: 200,
            minWidth: 180,
            aggFunc: this.createAvgAggFunc((row) => row?.avg_costs_in_eur?.human_per_rq),
            cellClass: 'text-gray-600 dark:text-gray-400',
            valueFormatter: (params: ValueFormatterParams) => {
              if (typeof params.value !== 'number') {
                return '';
              }
              return `${params.value.toFixed(1)} EUR`;
            },
          },
          {
            field: 'avg_costs_in_eur.ai_per_rq',
            headerName: 'Avg. AI costs per RQ',
            headerTooltip: 'Average AI costs per Reference Question.',
            columnGroupShow: 'open',
            width: 130,
            minWidth: 120,
            aggFunc: this.createAvgAggFunc((row) => row?.avg_costs_in_eur?.ai_per_rq),
            cellClass: 'text-gray-600 dark:text-gray-400',
            valueFormatter: (params: ValueFormatterParams) => {
              if (typeof params.value !== 'number') {
                return '';
              }
              return `${params.value.toFixed(1)} EUR`;
            },
          },
          {
            field: 'avg_costs_in_eur.human_per_dq',
            headerName: 'Avg. human costs per DQ',
            headerTooltip: 'Average human labor costs per Derivative Question.',
            columnGroupShow: 'open',
            width: 200,
            minWidth: 180,
            aggFunc: this.createAvgAggFunc((row) => row?.avg_costs_in_eur?.human_per_dq),
            cellClass: 'text-gray-600 dark:text-gray-400',
            valueFormatter: (params: ValueFormatterParams) => {
              if (typeof params.value !== 'number') {
                return '';
              }
              return `${params.value.toFixed(1)} EUR`;
            },
          },
          {
            field: 'avg_costs_in_eur.per_approved_rq',
            headerName: 'Avg. per Approved RQ',
            headerTooltip: 'Average total cost (human + AI) per Approved Reference Question (Human Approved).',
            columnGroupShow: 'open',
            width: 180,
            minWidth: 160,
            aggFunc: this.createAvgAggFunc((row) => row?.avg_costs_in_eur?.per_approved_rq),
            cellClass: 'text-gray-600 dark:text-gray-400',
            valueFormatter: (params: ValueFormatterParams) => {
              if (typeof params.value !== 'number') {
                return '';
              }
              return `${params.value.toFixed(1)} EUR`;
            },
          },
          {
            field: 'avg_costs_in_eur.ai_per_dq',
            headerName: 'Avg. AI costs per DQ',
            headerTooltip: 'Average AI costs per Derivative Question.',
            columnGroupShow: 'open',
            width: 130,
            minWidth: 120,
            aggFunc: this.createAvgAggFunc((row) => row?.avg_costs_in_eur?.ai_per_dq),
            cellClass: 'text-gray-600 dark:text-gray-400',
            valueFormatter: (params: ValueFormatterParams) => {
              if (typeof params.value !== 'number') {
                return '';
              }
              return `${params.value.toFixed(1)} EUR`;
            },
          },
          {
            field: 'avg_costs_in_eur.per_published_dq',
            headerName: 'Avg. per Published DQ',
            headerTooltip: 'Average total cost (human + AI) per Published Derivative Question (Published).',
            columnGroupShow: 'open',
            width: 180,
            minWidth: 160,
            aggFunc: this.createAvgAggFunc((row) => row?.avg_costs_in_eur?.per_published_dq),
            cellClass: 'text-gray-600 dark:text-gray-400',
            valueFormatter: (params: ValueFormatterParams) => {
              if (typeof params.value !== 'number') {
                return '';
              }
              return `${params.value.toFixed(1)} EUR`;
            },
          },
        ],
      },
    ];
  }

  getData(): Observable<{
    data: ContentAnalyticsData[];
    pinnedBottomRowData: ContentAnalyticsData[];
    isLoading: boolean;
  }> {
    const query = `
      SELECT *
      FROM \`examdojo-analytics.00_dbt_05_business_intelligence.bi__content_analytics\`
    `;

    return this.bigQueryService.queryAndTransform<Record<string, unknown>>(query).pipe(
      map((rawData) => {
        const data = this.mapToContentAnalyticsData(rawData);
        const totalsRow = this.calculateTotalsRow(data);

        // The return type of calculateTotalsRow includes optional fields, but we need to match ContentAnalyticsData
        // for the pinned bottom row data array. Casting as any or ensuring the totalsRow shape matches is required.
        // Since the totals row is a partial representation that works for display, we can safely include it.
        const pinnedBottomRowData = [totalsRow as ContentAnalyticsData];

        return {
          data,
          pinnedBottomRowData,
          isLoading: false,
        };
      }),
      startWith({ data: [], pinnedBottomRowData: [], isLoading: true }),
      catchError((error: unknown) => {
        console.error('Failed to load content analytics data:', error);
        return of({ data: [], pinnedBottomRowData: [], isLoading: false });
      }),
    );
  }

  fetchTableMetadata(): Observable<BigQueryTableMetadata> {
    return this.bigQueryService.fetchTableMetadata('bi__content_analytics');
  }

  calculateTotalsRow(
    data: ContentAnalyticsData[],
  ): Partial<ContentAnalyticsData> & { program: string; syllabus_short_name: string } {
    if (!data || data.length === 0) {
      return {
        program: 'TOTAL',
        syllabus_short_name: 'All Programs',
      };
    }

    const totalsRow: Partial<ContentAnalyticsData> & { program: string; syllabus_short_name: string } = {
      program: 'TOTAL',
      syllabus_short_name: 'All Programs',
    };

    // Aggregate the 4 key totals: Total Sources, RQs, DQs, and Costs

    // Total Sources - sum up all tiers and average the rates
    const tier1Sum = data.reduce((sum, row) => sum + (row.sources?.['Tier 1'] ?? 0), 0);
    const tier2Sum = data.reduce((sum, row) => sum + (row.sources?.['Tier 2'] ?? 0), 0);
    const tier3Sum = data.reduce((sum, row) => sum + (row.sources?.['Tier 3'] ?? 0), 0);

    const avgCompletionRate =
      data.reduce((sum, row) => sum + (row.sources?.completion_rate ?? 0), 0) / (data.length || 1);
    const avgParsedRate = data.reduce((sum, row) => sum + (row.sources?.parsed_rate ?? 0), 0) / (data.length || 1);
    const avgSuccessRate = data.reduce((sum, row) => sum + (row.sources?.success_rate ?? 0), 0) / (data.length || 1);

    totalsRow.sources = {
      'Tier 1': tier1Sum,
      'Tier 2': tier2Sum,
      'Tier 3': tier3Sum,
      completion_rate: avgCompletionRate,
      parsed_rate: avgParsedRate,
      success_rate: avgSuccessRate,
    };

    // Total RQs - get the latest value from each cumulative_rqs series and sum them
    const rqsSum = data.reduce((sum, row) => {
      if (row.cumulative_rqs && typeof row.cumulative_rqs === 'object') {
        const values = Object.values(row.cumulative_rqs).filter((v): v is number => typeof v === 'number');
        const latestValue = values.length > 0 ? Math.max(...values) : 0;
        return sum + latestValue;
      }
      return sum;
    }, 0);

    // Create a mock cumulative_rqs object for the totals row
    totalsRow.cumulative_rqs = { '2024-01-01': rqsSum };

    // Aggregate RQ data
    const totalRqsSum = data.reduce((sum, row) => sum + (row.reference_questions?.total_rqs ?? 0), 0);
    const avgRqCompletionRate =
      data.reduce((sum, row) => sum + (row.reference_questions?.completion_rate ?? 0), 0) / (data.length || 1);
    const avgRqSuccessRate =
      data.reduce((sum, row) => sum + (row.reference_questions?.success_rate ?? 0), 0) / (data.length || 1);

    totalsRow.reference_questions = {
      total_rqs: totalRqsSum,
      completion_rate: avgRqCompletionRate,
      success_rate: avgRqSuccessRate,
    };

    // Total DQs - get the latest value from each cumulative_dqs series and sum them
    const dqsSum = data.reduce((sum, row) => {
      if (row.cumulative_dqs && typeof row.cumulative_dqs === 'object') {
        const values = Object.values(row.cumulative_dqs).filter((v): v is number => typeof v === 'number');
        const latestValue = values.length > 0 ? Math.max(...values) : 0;
        return sum + latestValue;
      }
      return sum;
    }, 0);

    // Create a mock cumulative_dqs object for the totals row
    totalsRow.cumulative_dqs = { '2024-01-01': dqsSum };

    // Aggregate DQ data
    const totalDqsSum = data.reduce((sum, row) => sum + (row.derivative_questions?.total_dqs ?? 0), 0);
    const avgDqCompletionRate =
      data.reduce((sum, row) => sum + (row.derivative_questions?.q_completion_rate ?? 0), 0) / (data.length || 1);
    const avgDqSuccessRate =
      data.reduce((sum, row) => sum + (row.derivative_questions?.q_success_rate ?? 0), 0) / (data.length || 1);
    const avgPublishedDqsPerRq =
      data.reduce((sum, row) => sum + (row.derivative_questions?.avg_published_dqs_per_rq ?? 0), 0) /
      (data.length || 1);

    totalsRow.derivative_questions = {
      total_dqs: totalDqsSum,
      q_completion_rate: avgDqCompletionRate,
      q_success_rate: avgDqSuccessRate,
      avg_published_dqs_per_rq: avgPublishedDqsPerRq,
    };

    // Total Costs - sum the total_spend from all rows
    const totalSpendSum = data.reduce((sum, row) => sum + (row.total_spend ?? 0), 0);

    totalsRow.total_spend = totalSpendSum;

    return totalsRow;
  }

  private mapToContentAnalyticsData(rawData: Array<Record<string, unknown>>): ContentAnalyticsData[] {
    return rawData.map((rowData) => {
      // Helper to safely get nested object values
      const getObject = (key: string): Record<string, unknown> => {
        const value = rowData[key];
        return (value && typeof value === 'object' ? value : {}) as Record<string, unknown>;
      };

      // Get all nested objects
      const dates = getObject('dates');
      const scores = getObject('scores');
      const topics = getObject('topics');
      const sources = getObject('sources');
      const weeklyRqs = getObject('weekly_cumulative_rqs');
      const weeklyDqs = getObject('weekly_cumulative_dqs');
      const weeklyCosts = getObject('weekly_cumulative_costs_in_eur');
      const costs = getObject('costs');
      const coverage = getObject('coverage');
      const referenceQuestions = getObject('reference_questions');
      const derivativeQuestions = getObject('derivative_questions');

      // Helper to convert arrays to weekly_series object
      const arrayToWeeklySeries = (data: { dates?: unknown[]; counts?: unknown[] }): Record<string, number> => {
        if (!data || !data.dates || !data.counts) {
          return {};
        }

        const dateArray = Array.isArray(data.dates[0]) ? data.dates[0] : data.dates;
        const countArray = Array.isArray(data.counts[0]) ? data.counts[0] : data.counts;

        const result: Record<string, number> = {};
        dateArray.forEach((date: string, index: number) => {
          if (countArray[index] !== undefined) {
            result[date] = Number(countArray[index]) || 0;
          }
        });

        return result;
      };

      return {
        syllabus_id: Number(rowData['syllabus_id'] || 0),
        syllabus_short_name: String(rowData['syllabus_name'] || ''),
        program: String(rowData['program'] || ''),
        start_date: String(dates['start_date'] || ''),
        launch_date: String(dates['launch_date'] || ''),
        estimated_launch_date: String(dates['estimated_launch_date'] || ''),
        next_exam_session: String(dates['next_exam_session'] || ''),
        next_exam_session_estimated: Boolean(dates['next_exam_session_estimated'] ?? false),
        readiness_score: Number(scores['readiness_score'] || 0),
        completeness_score:
          scores['completeness_score'] !== null && scores['completeness_score'] !== undefined
            ? Number(scores['completeness_score'])
            : null,
        sources: {
          'Tier 1': Number(sources['tier_1'] || 0),
          'Tier 2': Number(sources['tier_2'] || 0),
          'Tier 3': Number(sources['tier_3'] || 0),
          completion_rate: Number(sources['source_completion_rate'] || 0),
          parsed_rate: Number(sources['source_parsed_rate'] || 0),
          success_rate: Number(sources['source_success_rate'] || 0),
        },
        cumulative_rqs: arrayToWeeklySeries(weeklyRqs),
        cumulative_dqs: arrayToWeeklySeries(weeklyDqs),
        reference_questions: {
          total_rqs: Number(referenceQuestions['total_rqs'] || 0),
          completion_rate: Number(referenceQuestions['rq_completion_rate'] || 0),
          success_rate: Number(referenceQuestions['rq_success_rate'] || 0),
        },
        derivative_questions: {
          total_dqs: Number(derivativeQuestions['total_dqs'] || 0),
          q_completion_rate: Number(derivativeQuestions['q_completion_rate'] || 0),
          q_success_rate: Number(derivativeQuestions['q_success_rate'] || 0),
          avg_published_dqs_per_rq: Number(derivativeQuestions['avg_published_dqs_per_rq'] || 0),
        },
        topics: {
          TL1: Number(topics['TL1'] || 0),
          TL2: Number(topics['TL2'] || 0),
          TL3: Number(topics['TL3'] || 0),
        },
        cumulative_costs_in_eur: arrayToWeeklySeries(weeklyCosts),
        total_spend: Number(costs['total_spend'] || 0),
        avg_costs_in_eur: {
          human_per_rq: Number(costs['avg_human_per_rq'] || 0),
          human_per_dq: Number(costs['avg_human_per_dq'] || 0),
          per_approved_rq: Number(costs['avg_per_approved_rq'] || 0),
          ai_per_rq: Number(costs['avg_ai_per_rq'] || 0),
          ai_per_dq: Number(costs['avg_ai_per_dq'] || 0),
          per_published_dq: Number(costs['avg_per_published_dq'] || 0),
        },
        coverage: {
          rq_to_tl3: Number(coverage['rq_to_tl3'] || 0),
          dq_to_tl3: Number(coverage['dq_to_tl3'] || 0),
          source_to_rq: Number(coverage['source_to_rq'] || 0),
          dq_to_rq: Number(coverage['dq_to_rq'] || 0),
        },
      } as ContentAnalyticsData;
    });
  }

  private createAvgAggFunc(valueGetter: (data: ContentAnalyticsData) => number | null | undefined) {
    return (params: IAggFuncParams<ContentAnalyticsData>) => {
      const rowDataItems =
        params.rowNode.allLeafChildren
          ?.map((child: IRowNode<ContentAnalyticsData>) => child.data)
          .filter((row): row is ContentAnalyticsData => row !== undefined) ?? [];
      const validValues = rowDataItems
        .map((row: ContentAnalyticsData) => valueGetter(row))
        .filter((val: number | null | undefined): val is number => typeof val === 'number' && !isNaN(val));

      if (validValues.length === 0) {
        return null;
      }

      return validValues.reduce((sum: number, val: number) => sum + val, 0) / validValues.length;
    };
  }

  private getProgressBarHTML(value: number, showPercentage = true): string {
    const percentage = Math.round(value * 100);
    let barColor: string;
    let textColor: string;

    if (percentage < 60) {
      barColor = 'bg-red-500 dark:bg-red-400';
      textColor = 'text-red-600 dark:text-red-400';
    } else if (percentage < 85) {
      barColor = 'bg-yellow-500 dark:bg-yellow-400';
      textColor = 'text-yellow-600 dark:text-yellow-400';
    } else {
      barColor = 'bg-green-500 dark:bg-green-400';
      textColor = 'text-green-600 dark:text-green-400';
    }

    const displayText = showPercentage ? `${percentage}%` : percentage.toString();

    return `<div class="flex items-center gap-2 h-full">
      <div class="w-16 bg-gray-200 dark:bg-gray-700 rounded-full h-1.5">
        <div class="${barColor} h-1.5 rounded-full transition-all duration-300" style="width: ${percentage}%"></div>
      </div>
      <span class="${textColor} font-semibold text-xs min-w-0">${displayText}</span>
    </div>`;
  }
}
